import { createApi } from '@reduxjs/toolkit/query/react';
import type { FilterDTOType, PromptTemplateDTOType } from '../../../schemas';
import type { PromptTemplateListDTOType } from '../../../schemas/styling';
import { stylistBaseQuery, stylistTagTypes } from './common';

export const stylistPromptTemplatesApi = createApi({
  reducerPath: 'stylistPromptTemplatesApi',
  baseQuery: stylistBaseQuery,
  tagTypes: [...stylistTagTypes],
  endpoints: builder => ({
    searchPromptTemplates: builder.infiniteQuery<
      Array<PromptTemplateListDTOType>,
      {
        filters?: Array<FilterDTOType>;
      },
      {
        page: number;
        size: number;
        filters: Array<FilterDTOType>;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          page: 1,
          size: 10,
          filters: [],
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage || lastPage.length === 0) {
            return undefined;
          }

          if (lastPage.length < lastPageParam.size) {
            return undefined;
          }

          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
            filters: lastPageParam.filters,
          };
        },
      },
      query: ({ pageParam, queryArg }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('page', pageParam.page.toString());
        searchParams.append('page_size', pageParam.size.toString());

        (queryArg.filters ?? []).forEach(filter => {
          searchParams.append(filter.field, filter.value);
        });

        return {
          url: `/prompts/prompt-templates/?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['PromptTemplate'],
    }),

    getPromptTemplateById: builder.query<PromptTemplateDTOType, number>({
      query: id => ({
        url: `/prompts/prompt-templates/${id}/`,
        method: 'GET',
      }),
      providesTags: ['PromptTemplate'],
    }),

    createPromptTemplate: builder.mutation<
      PromptTemplateDTOType,
      {
        key?: string;
        name: string;
        description: string;
        content: string;
        template_type?: PromptTemplateDTOType['template_type'];
        is_active?: boolean;
        model_config?: Record<string, unknown>;
        metadata?: Record<string, unknown>;
      }
    >({
      query: body => ({
        url: '/prompts/prompt-templates/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PromptTemplate'],
    }),

    updatePromptTemplate: builder.mutation<
      PromptTemplateDTOType,
      {
        id: number;
        key?: string;
        name: string;
        description: string;
        content: string;
        template_type?: PromptTemplateDTOType['template_type'];
        is_active?: boolean;
        model_config?: Record<string, unknown>;
        metadata?: Record<string, unknown>;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/prompts/prompt-templates/${id}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PromptTemplate'],
    }),

    duplicatePromptTemplate: builder.mutation<PromptTemplateDTOType, number>({
      query: id => ({
        url: `/prompts/prompt-templates/${id}/duplicate/`,
        method: 'POST',
      }),
      invalidatesTags: ['PromptTemplate'],
    }),
  }),
});

export const {
  useSearchPromptTemplatesInfiniteQuery,
  useGetPromptTemplateByIdQuery,
  useCreatePromptTemplateMutation,
  useUpdatePromptTemplateMutation,
  useDuplicatePromptTemplateMutation,
} = stylistPromptTemplatesApi;
